// Where Meta's checkout host sends the browser back to (#3454).
//
// Two routes, one component: the paths in `checkoutReturnRoutes.ts`. Those
// URLs are registered on the APNB app row (#3446) and must stay stable — the
// completion redirect matches a declared prefix
// (`https://console.dev.dreamwrite.com/`, D118981624), so renaming them is a
// coordinated change with APNB, not a local refactor.
//
// THE CENTRAL CONSTRAINT: landing here is not evidence of payment. It is an
// unauthenticated top-level navigation to a static URL that anyone can type,
// carrying no signature and no outcome we can verify. So this page grants
// nothing, calls no fulfilment endpoint, and does not say "payment
// successful" — it says the payment was SUBMITTED, and reports only what our
// own server already knows about the order.
//
// Credits are not granted here for a second, more mundane reason: nothing
// grants them anywhere yet. There is no order-status read, no entitlement
// check and no webhook from DCP (#3447 is the open contract question), which
// is why the "not added automatically" line below is the honest state of the
// world rather than a defensive hedge.

import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { api, ApiRequestError } from '../../api/client'
import { useAuth } from '../../contexts/AuthContext'
import { useBooleanFlagValue } from '../../hooks/useBooleanFlagValue'
import { useClientFlags } from '../../hooks/useClientFlags'
import {
  trackCheckoutReturnFail,
  trackCheckoutReturnSuccess,
  trackCheckoutReturnUnmatched,
} from '../../analytics/events'
import { createNadDebug } from '../../utils/debugLog'
import { canSeeBilling } from './billingAccess'
import { clearPendingCheckout, readPendingCheckout } from './checkoutReturnStore'
import { formatCredits, formatMoney, type CheckoutOrder } from './billingTypes'
import SandboxNotice from './SandboxNotice'

const log = createNadDebug('dw:billing-return')

export type CheckoutOutcome = 'success' | 'failure'

interface Props {
  outcome: CheckoutOutcome
}

export default function CheckoutReturnPage({ outcome }: Props) {
  const { t, i18n } = useTranslation('settings')
  const locale = i18n.language
  const navigate = useNavigate()
  const { user, isLoading: authLoading } = useAuth()
  const { isLoading: flagsLoading } = useClientFlags()
  const enabled = useBooleanFlagValue('billing_sandbox_enabled', false)
  const [params] = useSearchParams()

  const allowed = canSeeBilling(user)
  const guardReady = !authLoading && !flagsLoading

  // Which order was this? Prefer the id this tab stashed on its way out: we
  // wrote it, for this user, moments ago. The query parameter is a fallback for
  // the case where ECP appends one — unverified, and not something to depend on
  // — and it is equally safe to trust, because resolving it goes through an
  // authorized read that only ever returns the caller's own orders.
  const userId = user?.id ?? null
  const orderId = useMemo(() => {
    const stashed = userId === null ? null : readPendingCheckout(userId)
    if (stashed !== null) return stashed
    const fromUrl = params.get('order')
    return fromUrl !== null && fromUrl !== '' ? fromUrl : null
  }, [params, userId])

  const [order, setOrder] = useState<CheckoutOrder | null>(null)
  const [resolved, setResolved] = useState(false)
  // A 404 and a timeout are different facts and must not share a screen. The
  // first means the order is not ours; the second means we do not know, and the
  // order may well have been paid. Collapsing them told the user their payment
  // did not match anything, and told the funnel `foreign_order`.
  const [lookupFailed, setLookupFailed] = useState(false)

  useEffect(() => {
    if (!guardReady || !allowed || !enabled) return
    if (orderId === null) {
      setResolved(true)
      return
    }
    let cancelled = false
    void (async () => {
      try {
        const found = await api.getCheckoutOrder(orderId)
        if (!cancelled) setOrder(found)
      } catch (error) {
        // A 404 is ordinary — a stale stash from a previous session, or an id
        // that was never ours. Anything else (timeout, offline, 5xx) is a
        // failure to ASK, not an answer, and gets its own state.
        const notFound = error instanceof ApiRequestError && error.status === 404
        if (!cancelled) {
          setOrder(null)
          setLookupFailed(!notFound)
        }
      } finally {
        if (!cancelled) setResolved(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [allowed, enabled, guardReady, orderId])

  // checkout.return.* (#3503) — fired once the order lookup has settled, so
  // "matched" is a fact rather than a race. Success here means ARRIVAL, not
  // payment: this is an unauthenticated navigation to a static URL with no
  // signed outcome, so a funnel that counts revenue off it will over-count.
  const returnLoggedRef = useRef(false)
  useEffect(() => {
    if (!guardReady || !resolved || returnLoggedRef.current) return
    returnLoggedRef.current = true
    log({
      event: `checkout:return_${outcome}`,
      trace_id: orderId === null ? 'billing:unmatched' : `billing:${orderId}`,
      matched: order !== null,
    })
    // A failed lookup is NOT an unmatched return. When we still hold the order
    // id we know exactly which order the user came back from — we just could
    // not fetch its details — so the arrival is reported against that id rather
    // than being mislabelled as somebody else's order.
    const knownOrderId = order?.orderId ?? (lookupFailed ? orderId : null)
    if (knownOrderId === null) {
      // `has_order_id` separates "the browser told us nothing" from "it named
      // an order that is not ours" — different bugs behind the same screen.
      trackCheckoutReturnUnmatched({
        has_order_id: orderId !== null,
        reason: orderId === null ? 'unknown_order' : 'foreign_order',
      })
      return
    }
    if (outcome === 'success') {
      trackCheckoutReturnSuccess({ mft_order_id: knownOrderId })
    } else {
      trackCheckoutReturnFail({ mft_order_id: knownOrderId })
    }
  }, [guardReady, lookupFailed, order, orderId, outcome, resolved])

  useEffect(() => {
    if (!guardReady) return
    if (!allowed || !enabled) {
      log({ event: 'checkout:return_blocked', trace_id: 'billing:guard', allowed, enabled })
      navigate('/dashboard', { replace: true })
    }
  }, [allowed, enabled, guardReady, navigate])

  // The attempt is over either way, so the stash has done its job. Clearing it
  // on the way out stops a later, unrelated visit to this URL in the same tab
  // from resurrecting an order the user has already moved past.
  const goBackToCredits = () => {
    clearPendingCheckout()
    navigate('/dashboard?settings=usage-credits', { replace: true })
  }

  if (!guardReady || !resolved) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0b0b0d] text-sm text-white/50">
        {t('settingsModal.billing.loadingCheckout')}
      </div>
    )
  }
  if (!allowed || !enabled) return null

  const succeeded = outcome === 'success'

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0b0b0d] px-4 py-16">
      <div
        className="w-full max-w-[520px] rounded-3xl border border-white/[0.07] bg-[rgba(18,18,21,0.9)] p-6"
        data-testid={succeeded ? 'checkout-return-success' : 'checkout-return-failure'}
      >
        {/* TEST containers are the only ones this rail is pointed at outside
            prod, and the notice is what tells a tester no money moved. Shown
            when the mode is unknown too — silence would read as LIVE. */}
        {order?.paymentContainerMode !== 'LIVE' && <SandboxNotice className="mb-5" />}

        <h1 className="mb-3 text-xl font-semibold text-white">
          {succeeded
            ? t('settingsModal.billing.returnSuccessTitle')
            : t('settingsModal.billing.returnFailureTitle')}
        </h1>

        <p className="mb-4 text-sm text-white/70">
          {succeeded
            ? t('settingsModal.billing.returnSuccessBody')
            : t('settingsModal.billing.returnFailureBody')}
        </p>

        {order !== null && (
          <div className="mb-4 overflow-hidden rounded-2xl bg-white/[0.06]" data-testid="checkout-return-order">
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-sm text-white/50">{t('settingsModal.billing.product')}</span>
              <span className="text-sm text-white">
                {t('settingsModal.billing.packLabel', {
                  credits: formatCredits(order.credits, locale),
                })}
              </span>
            </div>
            {order.price !== null && (
              <div className="flex items-center justify-between border-t border-white/[0.06] px-4 py-3">
                <span className="text-sm text-white/50">{t('settingsModal.billing.total')}</span>
                <span className="text-sm font-medium text-white">
                  {formatMoney(order.price, locale)}
                </span>
              </div>
            )}
          </div>
        )}

        {order === null && (
          <p
            className="mb-4 text-sm text-white/45"
            data-testid={lookupFailed ? 'checkout-return-lookup-failed' : 'checkout-return-unmatched'}
          >
            {lookupFailed
              ? t('settingsModal.billing.returnLookupFailed')
              : t('settingsModal.billing.returnUnmatchedOrder')}
          </p>
        )}

        {/* Only on the success path. Saying it after a failure would imply a
            payment happened that we then failed to credit. */}
        {succeeded && (
          <p className="mb-5 text-sm text-white/45" data-testid="checkout-return-pending-credits">
            {t('settingsModal.billing.returnPendingCredits')}
          </p>
        )}

        <button
          type="button"
          data-testid="checkout-return-back"
          onClick={goBackToCredits}
          className="h-11 w-full rounded-full bg-white text-sm font-semibold text-black transition-colors hover:bg-white/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
        >
          {t('settingsModal.billing.backToCredits')}
        </button>
      </div>
    </div>
  )
}
